/**
 * Picks the `[data-theme]` value: the stored choice, else the OS preference,
 * else light. Nothing is stored until the user toggles, so a first visit keeps
 * following the OS.
 */

import { useCallback, useEffect, useState } from "react";
import { storageKeys } from "../config";

export type Theme = "light" | "dark";

export const THEME_STORAGE_KEY = storageKeys.theme;

const DARK_QUERY = "(prefers-color-scheme: dark)";

/** The user's stored choice, or null when they have never expressed one. */
export function storedTheme(): Theme | null {
  try {
    const raw = localStorage.getItem(storageKeys.theme);
    return raw === "light" || raw === "dark" ? raw : null;
  } catch {
    // Storage denied (private mode) is no error: the OS preference applies.
    return null;
  }
}

/** True when the OS asks for a dark UI. False whenever it cannot be asked. */
export function prefersDark(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  try {
    return window.matchMedia(DARK_QUERY).matches;
  } catch {
    return false;
  }
}

/** Theme to paint before the first frame. */
export function initialTheme(): Theme {
  return storedTheme() ?? (prefersDark() ? "dark" : "light");
}

/** `color-scheme` goes along so form controls, scrollbars and the page canvas follow the tokens. */
export function applyTheme(theme: Theme): void {
  const root = document.documentElement;
  root.setAttribute("data-theme", theme);
  root.style.colorScheme = theme;
}

export function storeTheme(theme: Theme): void {
  try {
    localStorage.setItem(storageKeys.theme, theme);
  } catch {
    // See storedTheme(): storage may be unavailable; the session still themes.
  }
}

/**
 * Applied on every change, stored only on an explicit set; with nothing stored it
 * follows the OS.
 */
export function useTheme(): [Theme, (next: Theme) => void] {
  const [theme, setThemeState] = useState<Theme>(initialTheme);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    let query: MediaQueryList;
    try {
      query = window.matchMedia(DARK_QUERY);
    } catch {
      return;
    }
    if (typeof query.addEventListener !== "function") return;
    const onChange = (event: MediaQueryListEvent) => {
      // An explicit choice outranks the OS — never overwrite it.
      if (storedTheme() !== null) return;
      setThemeState(event.matches ? "dark" : "light");
    };
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);

  const setTheme = useCallback((next: Theme) => {
    storeTheme(next);
    setThemeState(next);
  }, []);

  return [theme, setTheme];
}
